import React, { useCallback, useEffect, useRef, useState } from "react";

import { ChromePicker, CirclePicker, ColorResult } from "react-color";

import { useDocumentEvent } from "@culturehq/hooks";
import { classnames, PlainButton } from "@culturehq/components";

import { useDoor } from "app/hooks";
import { useProfile } from "app/context";
import { containedBy } from "app/utils";

import ColorFieldIcon from "components/common/icons/ColorFieldIcon";

import general from "constants/general";
import getRgbColor from "../utils/getRgbColor";
import { getOrganizationColors, hideChromePicker } from "../utils";

interface ColorPickerProps {
  disabled?: boolean;
  initialColor: ColorResult;
  onChangeColor: (color: ColorResult) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ disabled, initialColor, onChangeColor }) => {
  const { profile: { organization: { name: organization } } } = useProfile();

  const containerRef = useRef<HTMLDivElement>(null);
  const { open, onClose, onToggle } = useDoor(false);
  const [color, setColor] = useState<ColorResult>(general.DEFAULT_COLOR);
  const [rgbColor, setRgbColor] = useState("rgba(0,0,0,1)");

  const hideChrome = hideChromePicker(organization);
  const orgColors = getOrganizationColors(organization);

  useEffect(
    () => {
      setColor(initialColor);
      setRgbColor(getRgbColor(initialColor));
    }, [initialColor]
  );

  useDocumentEvent("click", useCallback(
    event => {
      const targetNode = event.target as Node;
      if (!containedBy(targetNode, "ColorField-Container") && containerRef.current && !containerRef.current.contains(targetNode)) {
        onClose();
      }
    },
    [onClose]
  ));

  const handleColorChange = (color: ColorResult) => {
    setColor(color);
    onChangeColor(color);
  };

  const handleDisplayClick = () => {
    onToggle();
  };

  return (
    <div ref={containerRef} className="ColorField-Container">
      <PlainButton className="ColorField-Btn" onClick={handleDisplayClick} disabled={disabled}>
        <ColorFieldIcon className="ColorField-Btn__Icon" />
        <div className="ColorField-Sample" style={{ backgroundColor: rgbColor }} />
        <span>{color.hex}</span>
      </PlainButton>
      {open && (
        <div className={classnames("Pickers-Container", hideChrome ? "Gray-Background" : "")}>
          {!hideChrome && <ChromePicker color={color.rgb} onChange={handleColorChange} />}
          <CirclePicker
            {...(hideChrome && { circleSpacing: 22 })}
            {...(hideChrome && { colors: orgColors })}
            color={color.rgb}
            onChange={handleColorChange}
          />
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
